import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Send, Mail, Phone, MessageSquare, User, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunicationPanel = ({ requestId, requestData }) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [formData, setFormData] = useState({
    type: 'email',
    from_party: 'admin',
    to_party: 'seeker',
    subject: '',
    message: ''
  });

  useEffect(() => {
    if (requestId) {
      fetchCommunications();
    }
  }, [requestId]);

  useEffect(() => {
    scrollToBottom();
  }, [communications]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchCommunications = async () => {
    try {
      const response = await axios.get(`${API}/admin/contact-requests/${requestId}/communications`);
      setCommunications(response.data.communications || []);
    } catch (error) {
      console.error('Error fetching communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.message.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/admin/contact-requests/${requestId}/communications`, formData);
      
      // Reset form
      setFormData({
        ...formData,
        subject: '',
        message: ''
      });
      
      // Refresh communications
      await fetchCommunications();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const getCommunicationIcon = (type) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'phone':
        return <Phone className="h-4 w-4" />;
      case 'chat':
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / 60000);
    
    if (diffMinutes < 1) return 'À l\'instant';
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    if (diffMinutes < 1440) return `Il y a ${Math.floor(diffMinutes / 60)} h`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPartyColor = (party) => {
    switch (party) {
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'professional':
        return 'bg-purple-100 text-purple-800';
      case 'seeker':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-[#FF9F68]" />
          Communication
        </h3>

        {/* Communication History */}
        <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
            </div>
          ) : communications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Aucune communication pour le moment</p>
            </div>
          ) : (
            <div className="space-y-4">
              {communications.map((comm, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg ${
                    comm.from_party === 'admin' 
                      ? 'bg-blue-50 border-l-4 border-blue-500' 
                      : 'bg-white border-l-4 border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={getPartyColor(comm.from_party)}>
                        <User className="h-3 w-3 mr-1" />
                        {comm.from_party === 'admin' ? 'Admin' : 
                         comm.from_party === 'professional' ? 'Professionnel' : 'Chercheur'}
                      </Badge>
                      <span className="text-sm text-gray-500">→</span>
                      <Badge className={getPartyColor(comm.to_party)}>
                        {comm.to_party === 'admin' ? 'Admin' : 
                         comm.to_party === 'professional' ? 'Professionnel' : 'Chercheur'}
                      </Badge>
                      <div className="flex items-center gap-1 text-gray-500">
                        {getCommunicationIcon(comm.type)}
                        <span className="text-xs capitalize">{comm.type}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      {formatDate(comm.date)}
                    </div>
                  </div>
                  
                  {comm.subject && (
                    <div className="font-semibold text-sm text-gray-900 mb-1">
                      {comm.subject}
                    </div>
                  )}
                  
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {comm.message}
                  </p>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* New Communication Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type de communication</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Téléphone</SelectItem>
                  <SelectItem value="chat">Chat</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Destinataire</Label>
              <Select
                value={formData.to_party}
                onValueChange={(value) => setFormData({ ...formData, to_party: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seeker">Chercheur ({requestData?.seeker_name})</SelectItem>
                  {requestData?.assigned_to_professional && (
                    <SelectItem value="professional">Professionnel</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.type === 'email' && (
            <div>
              <Label>Sujet</Label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9F68]"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Sujet de l'email"
              />
            </div>
          )}

          <div>
            <Label>Message</Label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Votre message..."
              rows={4}
              required
            />
          </div>

          <Button 
            type="submit" 
            disabled={sending || !formData.message.trim()}
            className="w-full"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? 'Envoi...' : 'Envoyer le message'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default CommunicationPanel;
